import { useState, useEffect } from "react";

// Base input styling class
const INPUT_BASE_CLASSES = "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100";

interface CurrencyFieldProps {
  value: number; // value in pennies
  onChange: (value: number) => void; // callback with value in pennies
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  currencySymbol?: string;
}

export function CurrencyField({
  value,
  onChange,
  placeholder,
  disabled = false,
  error = false,
  currencySymbol = "£"
}: CurrencyFieldProps) {
  const [displayValue, setDisplayValue] = useState("");

  // Convert pennies to display format
  const formatCurrency = (pennies: number): string => {
    if (pennies === 0) return "";
    const pounds = pennies / 100;
    return pounds.toLocaleString("en-GB", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };

  // Parse display value to pennies
  const parseCurrency = (displayStr: string): number => {
    if (!displayStr) return 0;
    const cleanStr = displayStr.replace(/[£$€,\s]/g, "");
    const pounds = parseFloat(cleanStr) || 0;
    return Math.round(pounds * 100);
  };

  // Update display value when prop value changes
  useEffect(() => {
    setDisplayValue(formatCurrency(value));
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setDisplayValue(inputValue);
    
    const pennies = parseCurrency(inputValue);
    onChange(pennies);
  };

  const handleBlur = () => {
    // Reformat on blur for consistency
    setDisplayValue(formatCurrency(value));
  };

  const errorClasses = error 
    ? "border-red-300 dark:border-red-600 focus:ring-red-500 focus:border-red-500" 
    : "";

  return (
    <div className="relative">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <span className="text-gray-500 dark:text-gray-400 sm:text-sm">{currencySymbol}</span>
      </div>
      <input
        type="text"
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        className={`${INPUT_BASE_CLASSES} pl-8 ${errorClasses} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      />
    </div>
  );
}
